import { D, calculate, solveSalePrice } from "@netkeep/fee-engine";
import type { CalculationInput, FeeSchedule } from "@netkeep/fee-types";
import { useId, useMemo, useState } from "react";
import { Badge, Card, Muted } from "@/components/ui";
import { TRANSACTION_LABEL, isNegative, money } from "@/lib/format";
import { useCalculator } from "@/state/calculator";
import { useFeeData } from "@/state/fee-data";

interface Comparison {
  schedule: FeeSchedule;
  netProfit: string | null;
  totalFees: string | null;
  breakEven: string | null;
  targetPrice: string | null;
  error: string | null;
}

/** Strips per-profile manual fees so every platform is compared on its own presets. */
function presetOnly(input: CalculationInput, currency: string): CalculationInput {
  const { overrides: _overrides, promotedListingPercent: _promoted, ...rest } = input;
  return { ...rest, currency };
}

export default function ComparePage() {
  const { schedules } = useFeeData();
  const { computation } = useCalculator();
  const [target, setTarget] = useState("");
  const targetId = useId();
  const targetValid = target.trim() === "" || /^-?\d+(\.\d{1,2})?$/.test(target.trim());
  const input = computation.input;

  const rows = useMemo<Comparison[]>(() => {
    if (!input) return [];
    return schedules
      .map((schedule) => {
        const base = presetOnly(input, schedule.currency);
        try {
          const r = calculate(base, schedule);
          const t = target.trim();
          return {
            schedule,
            netProfit: r.netProfit,
            totalFees: r.totalFees,
            breakEven: r.breakEvenPrice,
            targetPrice: t && targetValid ? solveSalePrice(base, schedule, t) : null,
            error: null,
          };
        } catch (err) {
          return {
            schedule,
            netProfit: null,
            totalFees: null,
            breakEven: null,
            targetPrice: null,
            error: err instanceof Error ? err.message : String(err),
          };
        }
      })
      .sort((a, b) =>
        a.netProfit == null ? 1 : b.netProfit == null ? -1 : new D(b.netProfit).comparedTo(new D(a.netProfit)),
      );
  }, [input, schedules, target, targetValid]);

  const bestId = rows[0]?.netProfit != null ? rows[0].schedule.id : null;
  const showTarget = target.trim() !== "" && targetValid;

  return (
    <>
      <h1 className="page-title">Compare</h1>
      <Muted>Your Calculator numbers on every platform, using each platform's preset fees.</Muted>

      <Card>
        <div className="field">
          <label className="field__label" htmlFor={targetId}>
            Target profit (optional)
          </label>
          <div className={targetValid ? "field__box" : "field__box field__box--error"}>
            <span className="field__affix" aria-hidden>
              $
            </span>
            <input
              id={targetId}
              className="field__input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g. 25.00"
              aria-invalid={targetValid ? undefined : true}
              aria-describedby={`${targetId}-hint`}
            />
          </div>
          <span id={`${targetId}-hint`} className={targetValid ? "muted" : "field__error"}>
            {targetValid ? "Shows the price you'd need on each platform." : "Enter an amount like 25 or 25.50."}
          </span>
        </div>
      </Card>

      {!input ? <Muted>Fix the highlighted inputs on the Calculator tab to see a comparison.</Muted> : null}

      {rows.length > 0 ? (
        <Card flush>
          <ol className="stack-list" aria-label="Platforms ranked by net profit">
            {rows.map((row) => {
              const c = row.schedule.currency;
              const details = row.error
                ? row.error
                : [
                    `Fees ${money(row.totalFees!, c)}`,
                    `Break-even ${row.breakEven ? money(row.breakEven, c) : "n/a"}`,
                    ...(showTarget ? [`Target ${row.targetPrice ? money(row.targetPrice, c) : "n/a"}`] : []),
                  ].join("  ·  ");
              return (
                <li key={row.schedule.id} className="item">
                  <div className="grow">
                    <div className="row row--tight">
                      <span className="item__title">{row.schedule.platform.name}</span>
                      {row.schedule.id === bestId ? <Badge label="Best" tone="accent" /> : null}
                    </div>
                    <div className="item__sub ellipsis">
                      {row.schedule.paymentMethod.name} · {TRANSACTION_LABEL[row.schedule.transactionType]}
                    </div>
                    <div className={row.error ? "text-error" : "item__details"}>{details}</div>
                  </div>
                  {row.netProfit != null ? (
                    <span
                      className={`item__profit ${isNegative(row.netProfit) ? "is-negative" : "is-profit"}`}
                      aria-label={`Net profit ${money(row.netProfit, c)}`}
                    >
                      {money(row.netProfit, c)}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </Card>
      ) : null}
    </>
  );
}
